import { MarketingHeader } from '@/components/marketing/MarketingHeader';
import { MarketingFooter } from '@/components/marketing/MarketingFooter';
import { BlogCard } from '@/components/blog/BlogCard';
import { BlogCategoryFilter } from '@/components/blog/BlogCategoryFilter';
import { BlogListStructuredData } from '@/components/blog/BlogStructuredData';
import { useSEO } from '@/hooks/useSEO';
import { getPostsByCategory } from '@/lib/blog';
import type { BlogCategory } from '@/lib/blogCategories';

interface BlogCategoryPageProps {
  category: BlogCategory;
}

export default function BlogCategoryPage({ category }: BlogCategoryPageProps) {
  const posts = getPostsByCategory(category.name);

  useSEO({
    title: `${category.name} — Blog | Beatview`,
    description: category.description,
    canonicalUrl: `https://beatview.ai/blog/${category.slug}`,
    ogType: 'website',
  });

  return (
    <div className="min-h-screen bg-bv-base text-bv-ink">
      <BlogListStructuredData url={`https://beatview.ai/blog/${category.slug}`} posts={posts} categoryName={category.name} />
      <MarketingHeader />

      <section className="relative overflow-hidden bg-bv-base pb-12 pt-36 lg:pt-44">
        <div aria-hidden className="bv-hero-grid pointer-events-none absolute inset-0" />
        <div className="relative container mx-auto px-4 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-bv-blue-line bg-white/80 px-3 py-1 text-[13px] font-semibold text-bv-blue"><span className="h-1.5 w-1.5 rounded-full bg-current" />Blog category</span>
          <h1 className="mt-6 font-display text-[40px] font-semibold leading-[1.02] tracking-[-0.04em] text-bv-ink sm:text-6xl">
            {category.name}
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-bv-ink2">
            {category.description}
          </p>
        </div>
      </section>

      <section className="bg-bv-base pb-24">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="mb-10">
            <BlogCategoryFilter activeSlug={category.slug} />
          </div>

          {posts.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <p className="text-lg">No articles in this category yet.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map(post => (
                <BlogCard
                  key={post.slug}
                  slug={post.slug}
                  title={post.title}
                  excerpt={post.excerpt}
                  coverImageUrl={post.cover}
                  category={post.category}
                  authorName={post.author}
                  publishedAt={post.publishedAt}
                  readingTime={post.readingTime}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
